import React, { useState } from 'react';
import { PatientFormData } from '../types/forms';

interface PatientFormProps {
  onSubmit: (data: PatientFormData) => Promise<void> | void;
  initialData?: PatientFormData;
}

const PatientForm: React.FC<PatientFormProps> = ({ onSubmit, initialData }) => {
  const [name, setName] = useState(initialData?.name ?? '');
  const [age, setAge] = useState(initialData?.age ?? '');
  const [species, setSpecies] = useState(initialData?.species ?? '');
  const [ownerName, setOwnerName] = useState(initialData?.ownerName ?? '');
  const [contact, setContact] = useState(initialData?.contact ?? '');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({ name, age, species, ownerName, contact });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-600">
          Patient name
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
            required
          />
        </label>
        <label className="text-sm font-medium text-slate-600">
          Age (years)
          <input
            type="number"
            value={age}
            onChange={(event) => setAge(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
            required
            min={0}
          />
        </label>
      </div>

      <label className="text-sm font-medium text-slate-600">
        Species
        <input
          type="text"
          value={species}
          onChange={(event) => setSpecies(event.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
          placeholder="Canine, Feline, Exotic..."
          required
        />
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-600">
          Caregiver name
          <input
            type="text"
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
            required
          />
        </label>
        <label className="text-sm font-medium text-slate-600">
          Contact number
          <input
            type="tel"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
            required
          />
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="reset"
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-900"
          onClick={() => {
            setName('');
            setAge('');
            setSpecies('');
            setOwnerName('');
            setContact('');
          }}
        >
          Clear
        </button>
        <button
          type="submit"
          className="rounded-xl bg-brand-blue px-5 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save patient'}
        </button>
      </div>
    </form>
  );
};

export default PatientForm;
